import React, { useRef, useEffect, useState } from 'react'
import { useCarousel } from '../CarouselProvider'
import { ButtonBack, ButtonNext } from './Buttons'
import { Wrapper, ImagesContainer, ImageItem } from './_styles'

interface ImageData {
  src: string
  alt?: string
  id?: string | number
}

interface CarouselProps {
  images: ImageData[]
  imageClassName?: string
  containerClassName?: string
  children?: React.ReactNode
  // Настройки размеров
  gap?: number // расстояние между элементами
  endOffset?: number // дополнительный отступ в конце для корректировки
}

const Carousel: React.FC<CarouselProps> = ({
  images,
  imageClassName,
  containerClassName,
  children,
  gap = 8,
  endOffset = 0,
}) => {
  const {
    currentImage,
    setCurrentImageProgrammatically,
    imageSize,
    setTotalImages,
    totalImages,
    lastProgrammaticChange,
  } = useCarousel()
  const [translateX, setTranslateX] = useState(0)
  const [touchStart, setTouchStart] = useState<number | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [shouldAutoPosition, setShouldAutoPosition] = useState(true)
  const [isInitialized, setIsInitialized] = useState(false)
  const [isOverflowing, setIsOverflowing] = useState(false)
  const imagesWrapperRef = useRef<HTMLDivElement>(null)
  const imagesContainerRef = useRef<HTMLDivElement>(null)
  const lastCurrentImageRef = useRef(currentImage)
  const lastProgrammaticChangeRef = useRef(lastProgrammaticChange)

  const getItemDimensions = () => {
    const containerSize = imageSize
    const itemWidth = containerSize + gap
    return { containerSize, itemWidth }
  }

  useEffect(() => {
    setTotalImages(images.length)
    setIsInitialized(false)
  }, [images.length, setTotalImages])

  // Инициализация позиции при первом рендере
  useEffect(() => {
    if (images.length > 0 && totalImages > 0 && !isInitialized) {
      const initializePosition = () => {
        if (imagesWrapperRef.current && imagesContainerRef.current) {
          const initialPosition = calculateCenteredPosition()
          setTranslateX(initialPosition)
          setIsInitialized(true)
        }
      }
      // Используем setTimeout для гарантии готовности DOM
      const timeoutId = setTimeout(initializePosition, 0)
      return () => clearTimeout(timeoutId)
    }
  }, [images.length, imageSize, totalImages, isInitialized])

  const calculateCenteredPosition = () => {
    if (
      !imagesWrapperRef.current ||
      !imagesContainerRef.current ||
      images.length === 0
    ) {
      return 0
    }

    const wrapperWidth = imagesWrapperRef.current.clientWidth
    const { itemWidth } = getItemDimensions()
    const totalWidth = images.length * itemWidth - gap

    if (wrapperWidth === 0 || totalWidth === 0) {
      return 0
    }

    // Если контент меньше контейнера - центрируем
    if (totalWidth <= wrapperWidth) {
      return (wrapperWidth - totalWidth) / 2
    }

    // Если контент больше - позиционируем активное изображение
    const currentPosition = currentImage * itemWidth
    const { containerSize } = getItemDimensions()
    const centerOffset = wrapperWidth / 2 - containerSize / 2

    let targetPosition = centerOffset - currentPosition

    // Ограничиваем позицию границами (используем те же границы что и в getScrollBounds)
    const maxTranslateX = 0
    const minTranslateX = -(totalWidth - wrapperWidth) - endOffset

    targetPosition = Math.max(
      minTranslateX,
      Math.min(maxTranslateX, targetPosition)
    )

    return targetPosition
  }

  useEffect(() => {
    const checkOverflow = () => {
      if (imagesWrapperRef.current && imagesContainerRef.current) {
        const wrapperWidth = imagesWrapperRef.current.offsetWidth
        const containerWidth = imagesContainerRef.current.scrollWidth

        const overflowing = containerWidth > wrapperWidth
        setIsOverflowing(overflowing)
      }
    }

    checkOverflow()

    window.addEventListener('resize', checkOverflow)

    return () => {
      window.removeEventListener('resize', checkOverflow)
    }
  }, [])

  // Автоматическое позиционирование при изменении currentImage (только программно)
  useEffect(() => {
    const isProgrammaticChange =
      lastProgrammaticChangeRef.current !== lastProgrammaticChange

    if (
      !isDragging &&
      (isProgrammaticChange || shouldAutoPosition) &&
      lastCurrentImageRef.current !== currentImage &&
      isInitialized
    ) {
      const newPosition = calculateCenteredPosition()
      setTranslateX(newPosition)
      setShouldAutoPosition(true) // Восстанавливаем автопозиционирование после программного изменения
    }

    lastCurrentImageRef.current = currentImage
    lastProgrammaticChangeRef.current = lastProgrammaticChange
  }, [
    currentImage,
    imageSize,
    images.length,
    isDragging,
    shouldAutoPosition,
    lastProgrammaticChange,
    isInitialized,
  ])

  // Обработчик изменения размера окна
  useEffect(() => {
    const handleResize = () => {
      if (!isDragging && shouldAutoPosition && isInitialized) {
        const newPosition = calculateCenteredPosition()
        setTranslateX(newPosition)
      }
    }

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [
    currentImage,
    imageSize,
    images.length,
    isDragging,
    shouldAutoPosition,
    isInitialized,
  ])

  // Вычисляем границы прокрутки
  const getScrollBounds = () => {
    if (!imagesWrapperRef.current || !imagesContainerRef.current) {
      return { minTranslateX: 0, maxTranslateX: 0 }
    }

    const wrapperWidth = imagesWrapperRef.current.clientWidth
    const { itemWidth } = getItemDimensions()
    const totalWidth = images.length * itemWidth - gap

    if (totalWidth <= wrapperWidth) {
      // Если контент помещается - оставляем центрированным
      const centeredPosition = (wrapperWidth - totalWidth) / 2
      return {
        minTranslateX: centeredPosition,
        maxTranslateX: centeredPosition,
      }
    }

    // Если контент больше контейнера
    const maxTranslateX = 0
    // Обеспечиваем полную видимость последнего изображения
    const minTranslateX = -(totalWidth - wrapperWidth) - endOffset

    return { minTranslateX, maxTranslateX }
  }

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX)
    setIsDragging(true)
    setShouldAutoPosition(false) // Отключаем автопозиционирование во время свайпа
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!touchStart || !isDragging) return

    const currentTouch = e.targetTouches[0].clientX
    const diff = currentTouch - touchStart
    const { minTranslateX, maxTranslateX } = getScrollBounds()

    // Новая позиция с учетом перетаскивания
    let newTranslateX = translateX + diff

    // Ограничиваем прокрутку границами
    newTranslateX = Math.max(
      minTranslateX,
      Math.min(maxTranslateX, newTranslateX)
    )

    setTranslateX(newTranslateX)
    setTouchStart(currentTouch)
  }

  const handleTouchEnd = () => {
    setTouchStart(null)
    setIsDragging(false)
  }

  if (images.length === 0) {
    return null
  }

  return (
    <Wrapper
      ref={imagesWrapperRef}
      className={containerClassName}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {isOverflowing && <ButtonBack />}
      <ImagesContainer
        ref={imagesContainerRef}
        translateX={translateX}
        gap={gap}
      >
        {images.map((image, index) => (
          <ImageItem
            key={image.id || index}
            size={imageSize}
            isActive={index === currentImage}
            className={imageClassName}
            onClick={() => {
              setCurrentImageProgrammatically(index)
            }}
          >
            <img src={image.src} alt={image.alt || `Image ${index + 1}`} />
          </ImageItem>
        ))}
      </ImagesContainer>
      {children}
      {isOverflowing && <ButtonNext />}
    </Wrapper>
  )
}

export { Carousel }
export type { CarouselProps, ImageData }
